import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as api from '@src/service/agent'
import * as ts from '@src/types/agent'
import { SUCCESS } from '@src/constants/errors'

const initialAgentState: {
  agentInfo: ts.AgentInfo | null
  styleAdjectives: { style: string[]; adjectives: string[] }
  kolData: { total: number; data: ts.Kol[] }
  postData: { total: number; data: ts.Post[] }
  kolReplyData: { total: number; data: ts.Post[] }
  selfReplyData: { total: number; data: ts.Post[] }
  myAgents: ts.AgentItem[]
  agentData: {
    total: number
    data: ts.AgentItem[]
  }
  agentRules: ts.TwitterRule
  manualPostRule: ts.ManualRule
  targetTopics: string[]
  postContent: { content: string; payload: ts.SandboxTweet } | null
  scenarioPost: ts.SandboxScenarioPost
  twitterProfileInfo: ts.TwitterProfileInfo | null
} = {
  agentInfo: null,
  styleAdjectives: { style: [], adjectives: [] },
  kolData: {
    total: 0,
    data: [],
  },
  myAgents: [],
  agentData: {
    total: 0,
    data: [],
  },
  postData: {
    total: 0,
    data: [],
  },
  kolReplyData: {
    total: 0,
    data: [],
  },
  selfReplyData: {
    total: 0,
    data: [],
  },
  agentRules: {
    id: 0,
    twitterTbId: 0,
    ruleId: 0,
    name: '',
    screenName: '',
    language: 'English',
    enableReply: true,
    enablePost: true,
    enableBlockedKeywords: false,
    enableImageForPost: false,
    blockedKeywords: [],
    dailyAutoTweetLimit: 3,
    dailyAutoKolReplyLimit: 10,
    dailyAutoSelfReplyLimit: 10,
    customizeAccessToken: '',
    customizeAccessSecret: '',
    customizeConsumerKey: '',
    customizeConsumerSecret: '',
    authType: '',
    useReplyContentLanguage: false,
    enableExchangeSpotPic: false,
    workingHoursStartTime: '',
    workingHoursEndTime: '',
    workingHoursTimezone: '',
    alwaysActive: false,
  },
  manualPostRule: {
    dailyPostLimit: 3,
    enableGenerateContent: false,
    enableBlockedKeywords: false,
    language: 'ENGLISH',
    id: 0,
    blockedKeywords: [],
    telegramAvatar: '',
    telegramTbId: 0,
    telegramUsername: '',
    enablePostToTelegram: false,
    enableImageForPost: false,
    enableExchangeSpotPic: false,
    workingHoursStartTime: '',
    workingHoursEndTime: '',
    workingHoursTimezone: '',
    alwaysActive: false,
  },
  targetTopics: [],
  postContent: null,
  scenarioPost: { content: '', quoteTweetId: '' },
  twitterProfileInfo: null,
}

export const getMyPosts = createAsyncThunk(
  'agent/getMyPosts',
  async (params: { page?: number; agentId: number }) => {
    const { code, data } = await api.myPost(params)
    if (code === SUCCESS) {
      return data
    }
    return { data: [], total: 0 }
  },
)

export const getMyKolReplys = createAsyncThunk(
  'agent/getMyKolReplys',
  async (params: { page?: number; agentId: number }) => {
    const { code, data } = await api.kolReply(params)
    if (code === SUCCESS) {
      return data
    }
    return { data: [], total: 0 }
  },
)

export const getMySelfReplys = createAsyncThunk(
  'agent/getMySelfReplys',
  async (params: { page?: number; agentId: number }) => {
    const { code, data } = await api.selfReply(params)
    if (code === SUCCESS) {
      return data
    }
    return { data: [], total: 0 }
  },
)

export const fetchKols = createAsyncThunk(
  'agent/fetchKols',
  async (params: { page?: number; agentId: number }) => {
    const { code, data } = await api.getKOLs(params)
    if (code === SUCCESS) {
      return data
    }
    return { data: [], total: 0 }
  },
)

export const fecthStyleAbjectives = createAsyncThunk('agent/getStyleAbjectives', async () => {
  const { data } = await api.getStyleAdjectives()
  return data
})

export const fecthAgentInfo = createAsyncThunk('agent/getAgentInfo', async (id: number) => {
  const { data } = await api.getAgentInfo(id)
  return data
})

export const fetchMyAgents = createAsyncThunk('agent/fetchMyagents', async () => {
  const { code, data } = await api.getMyAgents()
  if (code === SUCCESS && Array.isArray(data)) {
    return data
  }
  return []
})

export const fetchAllAgents = createAsyncThunk(
  'agent/fetchAllagents',
  async (params: { page?: number; name?: string }) => {
    const { code, data } = await api.getAllAgents(params)
    if (code === SUCCESS) {
      return { data: data.entities, total: data.total }
    }
    return { data: [], total: 0 }
  },
)

export const fetchAgentRule = createAsyncThunk('agent/fetchAgentRule', async (agentId: number) => {
  const { data } = await api.getAgentRule(agentId)
  return data
})

export const getManualPostRule = createAsyncThunk(
  'agent/getManualPostRule',
  async (agentId: number) => {
    const { data } = await api.getManualPostRule(agentId)
    return data
  },
)

export const getTargetTopics = createAsyncThunk('agent/getTargetTopics', async () => {
  const { code, data } = await api.getTargetTopics()
  if (code === SUCCESS) {
    return data.topics
  }
  return []
})

export const fetchSandboxPost = createAsyncThunk(
  'agent/fetchSandboxPost',
  async (params: { agentId: number; language: string }) => {
    const { data } = await api.sandboxPost(params)
    return data
  },
)

export const fetchTwitterProfileInfo = createAsyncThunk(
  'agent/fetchTwitterProfileInfo',
  async (agentId: number) => {
    const { data } = await api.getTwitterProfile(agentId)
    return data
  },
)

export const saveTwitterProfileInfo = createAsyncThunk(
  'agent/saveTwitterProfileInfo',
  async (profileInfo: ts.TwitterProfileInfo & { agentId: number }) => {
    const response = await api.updateTwitterProfile(profileInfo)
    return response
  },
)

const agent = createSlice({
  name: 'agent',
  initialState: initialAgentState,
  extraReducers(builder) {
    builder
      .addCase(getMyPosts.fulfilled, (state, action) => {
        state.postData = action.payload
      })
      .addCase(getMyKolReplys.fulfilled, (state, action) => {
        state.kolReplyData = action.payload
      })
      .addCase(getMySelfReplys.fulfilled, (state, action) => {
        state.selfReplyData = action.payload
      })
      .addCase(fetchKols.fulfilled, (state, action) => {
        state.kolData = action.payload
      })
      .addCase(fecthStyleAbjectives.fulfilled, (state, action) => {
        state.styleAdjectives = action.payload
      })
      .addCase(fecthAgentInfo.fulfilled, (state, action) => {
        state.agentInfo = action.payload
      })
      .addCase(fetchMyAgents.fulfilled, (state, action) => {
        state.myAgents = action.payload
      })
      .addCase(fetchAllAgents.fulfilled, (state, action) => {
        state.agentData = action.payload
      })
      .addCase(fetchAgentRule.fulfilled, (state, action) => {
        state.agentRules = action.payload
      })
      .addCase(getManualPostRule.fulfilled, (state, action) => {
        state.manualPostRule = action.payload
      })
      .addCase(getTargetTopics.fulfilled, (state, action) => {
        state.targetTopics = action.payload
      })
      .addCase(fetchSandboxPost.fulfilled, (state, action) => {
        state.postContent = action.payload
      })

      .addCase(fetchTwitterProfileInfo.fulfilled, (state, action) => {
        state.twitterProfileInfo = action.payload
      })
  },
  reducers: {
    updateAgentInfo: (state, action) => {
      state.agentInfo = action.payload
    },
    updateAgentRules: (state, action) => {
      state.agentRules = action.payload
    },
    updateManualRules: (state, action) => {
      state.manualPostRule = action.payload
    },
    updatePostContent: (state, action) => {
      state.postContent = action.payload
    },
    updateScenarioPost: (state, action) => {
      state.scenarioPost = action.payload
    },
    updateTwitterProfileInfo: (state, action) => {
      state.twitterProfileInfo = action.payload
    },
  },
})

export const fetchCreateAgent = createAsyncThunk(
  'agent/createAgent',
  async (data: Omit<ts.Agent, 'id'>) => {
    const response = await api.createAgent(data)
    return response
  },
)
export const editAgent = createAsyncThunk('agent/editAgent', async (data: Partial<ts.Agent>) => {
  const { code } = await api.updateAgent(data)
  return code
})

export const fetchCloneTwitterAgent = createAsyncThunk(
  'agent/cloneTwitterAgent',
  async (params: ts.CloneTwitterAgentParams) => {
    const response = await api.cloneTwitterAgent(params)
    return response
  },
)

export const addKol = createAsyncThunk(
  'agent/addKol',
  async (data: { kols: string[]; agentId: number }) => {
    const { code } = await api.addKOL(data)
    if (code === SUCCESS) {
      return code
    }
    return code
  },
)
export const updateKol = createAsyncThunk('agent/updateKOLApi', async (kol: ts.EditKol) => {
  const { code } = await api.updateKOL(kol)
  if (code === SUCCESS) {
    return code
  }
  return code
})

export const delKol = createAsyncThunk('agent/delKol', async (id: number) => {
  const { code } = await api.deleteKOL(id)
  if (code === SUCCESS) {
    return code
  }
  return code
})

export const editTwitterRule = createAsyncThunk(
  'agent/editTwitterRule',
  async (data: ts.EditTwitterRule) => {
    const response = await api.editTwitterRule(data)
    return response
  },
)

export const fetchSandboxSelfReply = createAsyncThunk(
  'agent/fetchSandboxSelfReply',
  async (params: ts.SandboxSelfReply) => {
    const { data } = await api.sandboxSelfReply(params)
    return data
  },
)
export const getScenarioPost = createAsyncThunk(
  'agent/getScenarioPost',
  async (params: { agentId: number; topic: string; language: string }) => {
    const { data } = await api.getScenarioPost(params)
    return data
  },
)

export const fetchScheduleInfo = createAsyncThunk(
  'agent/getScheduleInfo',
  async (agentId: number) => {
    const { data } = await api.getScheduleInfo(agentId)
    return data
  },
)

export const generatePresignedUrl = createAsyncThunk(
  'agent/generatePresignedUrl',
  async (fileType: string) => {
    const { data } = await api.generatePresignedUrl(fileType)
    return data
  },
)

export const postNow = createAsyncThunk('agent/postNow', async (agentId: number) => {
  const response = await api.postNow(agentId)
  return response
})

export const replyQueueKol = createAsyncThunk('agent/replyQueueKol', async (agentId: number) => {
  const response = await api.replyQueueKol(agentId)
  return response
})
export const replyQueueSelf = createAsyncThunk('agent/replyQueueSelf', async (agentId: number) => {
  const response = await api.replyQueueSelf(agentId)
  return response
})

export const fetchCheckProjectPromotionUrls = createAsyncThunk(
  'agent/checkProjectPromotionUrls',
  async (urls: string[]) => {
    const response = await api.checkProjectPromotionUrls(urls)
    return response
  },
)
export const getProjectContent = createAsyncThunk(
  'agent/getProjectContent',
  async (url: string) => {
    const response = await api.getProjectContent(url)
    return response
  },
)

export const checkUserExists = createAsyncThunk(
  'agent/checkUserExists',
  async (usernames: string[]) => {
    const { data } = await api.checkUserExists(usernames)
    return data
  },
)

export const delAgent = createAsyncThunk('agent/delAgent', async (agentId: number) => {
  const { code } = await api.delAgent(agentId)
  return code
})

export const getTelegramLink = createAsyncThunk(
  'agent/getTelegramLink',
  async (agentId: number) => {
    const { data } = await api.getTelegramLink(agentId)
    return data
  },
)

export const {
  updateAgentInfo,
  updateAgentRules,
  updateManualRules,
  updatePostContent,
  updateScenarioPost,
  updateTwitterProfileInfo,
} = agent.actions
export default agent.reducer
